import type {
  ChatMessage,
  ConnStatus,
  OutMessage,
  SendStatus,
  SessionState,
} from '~/components/Webchat/types'

const MAX_MESSAGES = 200
const SIGNATURE_WINDOW = 20

const signature = (m: { from: string; text: string; at: string }) =>
  `${m.from}|${m.text}|${m.at}`

export type WebchatState = {
  connection: ConnStatus
  sessionState: SessionState
  assignedName: string | null
  messages: ChatMessage[]
  unreadCount: number
  panelOpen: boolean
  terminalError: string | null
  recentSignatures: string[] // ring buffer for inbound dedupe
}

export const initialWebchatState: WebchatState = {
  connection: 'idle',
  sessionState: 'OPEN',
  assignedName: null,
  messages: [],
  unreadCount: 0,
  panelOpen: false,
  terminalError: null,
  recentSignatures: [],
}

export type WebchatAction =
  | { type: 'CONNECTING' }
  | { type: 'CONNECTED' }
  | { type: 'RECONNECTING' }
  | { type: 'DISCONNECTED' }
  | { type: 'SESSION'; name: string }
  | { type: 'HISTORY'; messages: OutMessage[]; state: SessionState }
  | { type: 'SEND'; clientTempId: string; text: string; at: string }
  | { type: 'ACK_OK'; clientTempId: string; id: string; status: SendStatus }
  | { type: 'ACK_ERR'; clientTempId: string; error: string }
  | { type: 'INBOUND'; key: string; msg: OutMessage }
  | { type: 'OPEN_PANEL' }
  | { type: 'CLOSE_PANEL' }
  | { type: 'TERMINAL_ERROR'; message: string }

const cap = (msgs: ChatMessage[]): ChatMessage[] =>
  msgs.length > MAX_MESSAGES ? msgs.slice(msgs.length - MAX_MESSAGES) : msgs

export function webchatReducer(
  state: WebchatState,
  action: WebchatAction,
): WebchatState {
  switch (action.type) {
    case 'CONNECTING':
      return { ...state, connection: 'connecting' }
    case 'CONNECTED':
      return { ...state, connection: 'connected' }
    case 'RECONNECTING':
      return { ...state, connection: 'reconnecting' }
    case 'DISCONNECTED':
      return { ...state, connection: 'disconnected' }
    case 'SESSION':
      return { ...state, assignedName: action.name }
    case 'HISTORY':
      return {
        ...state,
        sessionState: action.state,
        messages: cap(
          action.messages.map((m) => ({
            ...m,
            key: m.id ?? `${m.from}|${m.at}`,
          })),
        ),
      }
    case 'SEND':
      return {
        ...state,
        messages: cap([
          ...state.messages,
          {
            key: action.clientTempId,
            clientTempId: action.clientTempId,
            from: 'user',
            text: action.text,
            at: action.at,
            status: 'PENDING',
          },
        ]),
      }
    case 'ACK_OK':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.clientTempId === action.clientTempId
            ? { ...m, id: action.id, status: action.status }
            : m,
        ),
      }
    case 'ACK_ERR':
      return {
        ...state,
        sessionState:
          action.error === 'session closed' ? 'CLOSED' : state.sessionState,
        messages: state.messages.map((m) =>
          m.clientTempId === action.clientTempId
            ? { ...m, status: 'FAILED' }
            : m,
        ),
      }
    case 'INBOUND': {
      const sig = signature(action.msg)
      if (state.recentSignatures.includes(sig)) return state
      // An admin reply auto-opens the panel so the visitor sees it; system
      // notices do not steal focus and only bump the unread badge.
      const panelOpen = state.panelOpen || action.msg.from === 'admin'
      return {
        ...state,
        messages: cap([...state.messages, { ...action.msg, key: action.key }]),
        panelOpen,
        unreadCount: panelOpen ? 0 : state.unreadCount + 1,
        recentSignatures: [...state.recentSignatures, sig].slice(
          -SIGNATURE_WINDOW,
        ),
      }
    }
    case 'OPEN_PANEL':
      return { ...state, panelOpen: true, unreadCount: 0 }
    case 'CLOSE_PANEL':
      return { ...state, panelOpen: false }
    case 'TERMINAL_ERROR':
      return { ...state, terminalError: action.message }
    default:
      return state
  }
}
